import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiArrowLeft, FiSave } from "react-icons/fi";
import { shopClient, eurCents, fmtDateTime, type ShopQuoteDetail } from "./shopClient";
import { QUOTE_STATUS } from "./Quotes";

const STATUSES = ["New", "Quoted", "Won", "Lost"];

/** Detail + workflow for one B2B quote (devis): customer/company/VAT, requested references, admin notes,
 *  quoted total, and status transitions. */
export default function QuoteDetail(): React.ReactElement {
	const { id } = useParams<{ id: string }>();
	const history = useHistory();
	const [quote, setQuote] = useState<ShopQuoteDetail | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	const [status, setStatus] = useState("New");
	const [notes, setNotes] = useState("");
	const [totalEuros, setTotalEuros] = useState("");

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const q = await shopClient.quotes.get(id);
			setQuote(q);
			setStatus(q.status);
			setNotes(q.adminNotes ?? "");
			setTotalEuros(q.quotedTotalCents != null ? (q.quotedTotalCents / 100).toFixed(2) : "");
		} catch (e: any) {
			setError(e?.message ?? "Erreur");
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => { void load(); }, [load]);

	const save = useCallback(async () => {
		setSaving(true);
		try {
			const cents = totalEuros.trim() === "" ? null : Math.round(parseFloat(totalEuros.replace(",", ".")) * 100);
			const updated = await shopClient.quotes.update(id, { status, adminNotes: notes, quotedTotalCents: cents });
			setQuote(updated);
			toast.success("Devis mis à jour");
		} catch (e: any) {
			toast.error(e?.message ?? "Erreur");
		} finally {
			setSaving(false);
		}
	}, [id, status, notes, totalEuros]);

	if (loading) return <div className="p-6 text-slate-400">Chargement…</div>;
	if (error || !quote) return <div className="p-6"><div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error ?? "Devis introuvable"}</div></div>;

	const s = QUOTE_STATUS[quote.status] ?? { label: quote.status, cls: "bg-slate-100 text-slate-600" };

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full max-w-5xl space-y-6">
				<button type="button" onClick={() => history.push("/quotes")} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800">
					<FiArrowLeft /> Retour aux devis
				</button>

				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Devis</p>
						<h1 className="text-2xl font-semibold text-slate-900">{quote.company || quote.customerName || "Demande de devis"}</h1>
						<p className="mt-1 text-sm text-slate-500">Reçu le {fmtDateTime(quote.createdAt)}</p>
					</div>
					<span className={`ml-auto rounded-full px-3 py-1 text-xs font-semibold ${s.cls}`}>{s.label}</span>
				</div>

				<div className="grid gap-6 lg:grid-cols-3">
					<div className="space-y-6 lg:col-span-2">
						<Card title="Références demandées">
							<table className="min-w-full divide-y divide-slate-100 text-sm">
								<thead><tr>
									<th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Référence</th>
									<th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Produit</th>
									<th className="px-4 py-2 text-center text-[11px] font-semibold uppercase tracking-widest text-slate-500">Qté</th>
								</tr></thead>
								<tbody className="divide-y divide-slate-100">
									{quote.lines.map((l) => (
										<tr key={l.id}>
											<td className="px-4 py-2 font-mono text-slate-800">{l.productRef}</td>
											<td className="px-4 py-2 text-slate-600">{l.productTitle || <span className="text-slate-400">non catalogue</span>}{l.note && <span className="block text-xs text-slate-400">{l.note}</span>}</td>
											<td className="px-4 py-2 text-center text-slate-700">{l.quantity}</td>
										</tr>
									))}
								</tbody>
							</table>
						</Card>

						{quote.message && (
							<Card title="Message du client">
								<p className="whitespace-pre-wrap text-sm text-slate-700">{quote.message}</p>
							</Card>
						)}

						<Card title="Traitement">
							<div className="space-y-4">
								<div>
									<label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-500">Statut</label>
									<select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none">
										{STATUSES.map((st) => <option key={st} value={st}>{(QUOTE_STATUS[st]?.label) ?? st}</option>)}
									</select>
								</div>
								<div>
									<label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-500">Total devisé (€ TTC)</label>
									<input value={totalEuros} onChange={(e) => setTotalEuros(e.target.value)} inputMode="decimal" placeholder="ex. 1290.00"
										className="w-48 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none" />
								</div>
								<div>
									<label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-500">Notes internes</label>
									<textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
										className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none" />
								</div>
								<button type="button" onClick={save} disabled={saving}
									className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-5 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
									<FiSave /> {saving ? "Enregistrement…" : "Enregistrer"}
								</button>
							</div>
						</Card>
					</div>

					<div className="space-y-6">
						<Card title="Client">
							<dl className="space-y-2 text-sm">
								<Row label="Entreprise" value={quote.company} />
								<Row label="N° TVA" value={quote.vatNumber} />
								<Row label="Contact" value={quote.customerName} />
								<Row label="E-mail" value={quote.customerEmail} />
								<Row label="Téléphone" value={quote.phone} />
								<Row label="Compte" value={quote.userId ? "Client connecté" : "Invité"} />
							</dl>
						</Card>
						{quote.quotedTotalCents != null && (
							<Card title="Devis">
								<div className="text-2xl font-semibold text-emerald-600">{eurCents(quote.quotedTotalCents)}</div>
							</Card>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
			<div className="border-b border-slate-100 px-5 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">{title}</div>
			<div className="p-5">{children}</div>
		</div>
	);
}

function Row({ label, value }: { label: string; value?: string | null }) {
	return (
		<div className="flex justify-between gap-3">
			<dt className="text-slate-500">{label}</dt>
			<dd className="text-right font-medium text-slate-800">{value || "—"}</dd>
		</div>
	);
}
